<template>
  <template v-if="loaded">
    <div class="h-[calc(100vh-178px)] flex gap-x-4">
      <ContentBlock class="basis-60" title="Filters">
        <div class="p-4 flex flex-col gap-y-4">
          <div class="flex rounded-lg border border-zinc-400 dark:border-zinc-600 overflow-hidden text-sm" role="group" aria-label="Data Source" v-if="hasCv">
            <button
              type="button"
              class="flex-1 px-3 py-2 font-medium transition-colors disabled:cursor-wait"
              :class="s === source ? 'bg-accent text-zinc-50' : 'hover:bg-zinc-50/5 text-zinc-500 dark:text-zinc-400'"
              :disabled="busy"
              v-for="s in dataSources"
              :key="s"
              @click="setSource(s)">
              {{ DATA_SOURCE_LABELS[s] }}
            </button>
          </div>
          <SingleObjectSelect label="Month" v-model="filter.month" :options="months" placeholder="Select Month" inline />
          <MultipleSelect label="Trip Purposes" v-model="filter.tripPurposes" :options="tripPurposes" inline v-if="!isCv" />
          <MultipleSelect label="Day Aggregation" v-model="filter.dayAgg" :options="dayAggs" inline />
          <MultipleSelect label="Hour Aggregation" v-model="filter.hourAgg" :options="hourAggs" :option-label="formatHourAgg" inline />
          <div
            class="mt-8 flex text-zinc-50 space-x-2 items-center justify-center px-3 py-2 rounded-lg border border-accent bg-accent/75 hover:bg-accent text-sm cursor-pointer"
            :class="{ 'pointer-events-none opacity-50': busy }"
            @click="applyFilter">
            <span>Apply</span>
          </div>
        </div>
      </ContentBlock>
      <div class="grow rounded-lg overflow-hidden h-full relative" ref="mapElement">
        <div class="absolute bottom-4 right-4 w-40 bg-zinc-100 dark:bg-zinc-800 rounded-md shadow">
          <div class="rounded-t-md bg-zinc-200 dark:bg-zinc-700 p-2 flex justify-between items-center">
            <h5 class="font-medium text-sm">Legend</h5>
          </div>
          <div class="p-2 text-sm">
            <div class="text-xs text-zinc-500 dark:text-zinc-400 mb-1 truncate" :title="legend.title">{{ legend.title }}</div>
            <ul>
              <li class="flex items-center justify-between gap-x-2" v-for="item in legend.items" :key="item.threshold">
                <div class="h-3.5 w-3.5 rounded-full shrink-0" :style="`background-color: ${item.color}`"></div>
                <div>{{ formatNumber(item.threshold) }}+</div>
              </li>
            </ul>
          </div>
        </div>
        <!-- selected zone details -->
        <div v-if="selection"
          class="bg-zinc-100 dark:bg-zinc-800 px-4 py-3 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-md absolute right-4 top-4 w-72 text-sm max-h-[calc(100%-2rem)] overflow-y-auto"
          role="dialog" aria-label="Selected zone">
          <div class="flex items-start gap-x-2 mb-2">
            <div class="min-w-0">
              <div class="font-semibold leading-tight truncate" :title="selection.name">{{ selection.name }}</div>
              <div v-if="selection.name !== selection.zoneId" class="text-xs text-zinc-500 dark:text-zinc-400">Zone {{ selection.zoneId }}</div>
            </div>
            <button type="button" class="ml-auto shrink-0 text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 size-4"
              aria-label="Close" @click="clearSelection">
              <IconClose class="size-4" />
            </button>
          </div>
          <dl class="tabular-nums">
            <div class="flex justify-between gap-x-2 py-0.5 border-t border-zinc-200 dark:border-zinc-700">
              <dt>Total trips</dt>
              <dd class="font-semibold">{{ formatNumber(selection.total) }}</dd>
            </div>
            <div class="flex justify-between gap-x-2 py-0.5">
              <dt class="text-zinc-500 dark:text-zinc-400">Origin</dt>
              <dd>{{ formatNumber(selection.outbound) }}</dd>
            </div>
            <div class="flex justify-between gap-x-2 py-0.5">
              <dt class="text-zinc-500 dark:text-zinc-400">Destination</dt>
              <dd>{{ formatNumber(selection.inbound) }}</dd>
            </div>
            <div class="flex justify-between gap-x-2 py-0.5" title="Intra-zonal trips start and end in this zone, so they count as both an origin and a destination">
              <dt class="text-zinc-500 dark:text-zinc-400">Intra-zonal</dt>
              <dd>{{ formatNumber(selection.intrazonal) }}</dd>
            </div>
            <div class="flex justify-between gap-x-2 py-0.5 border-t border-zinc-200 dark:border-zinc-700">
              <dt>Connected zones</dt>
              <dd class="font-semibold">{{ formatNumber(selection.connected) }}</dd>
            </div>
          </dl>
          <div v-if="!selection.connected" class="mt-2 text-xs text-zinc-500 dark:text-zinc-400">No trips to or from other zones</div>
        </div>
      </div>
      <ContentBlock class="basis-60">
        <div class="p-2 flex flex-col gap-y-8 justify-between">
          <div v-if="!isCv">
            <div class="pb-1 font-medium text-lg">Trips by Trip Purpose</div>
            <div>
              <Bar :data="purposeChartData" :options="chartOptions" />
            </div>
          </div>
          <div>
            <div class="pb-1 font-medium text-lg">Trips by Day Aggregation</div>
            <div>
              <Bar :data="dayAggChartData" :options="chartOptions" />
            </div>
          </div>
          <div>
            <div class="pb-1 font-medium text-lg">Trips by Hour Aggregation</div>
            <div>
              <Bar :data="hourAggChartData" :options="chartOptions" />
            </div>
          </div>
        </div>
      </ContentBlock>
    </div>
    <div>
      <ContentBlock>
        <div class="px-4 pt-1 pb-3 font-medium text-xl">Top 20 Trip Matrix</div>
        <MatrixHeatmap :data="dataMap.heatmap" />
      </ContentBlock>
    </div>
  </template>
  <div class="h-[calc(100vh-178px)] flex place-items-center justify-center" v-else>
    <IconSpinner class="animate-spin w-12 h-12" />
  </div>
</template>

<script setup>
import { ref, computed, onBeforeUnmount, onMounted, nextTick } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { useAuth0 } from "@auth0/auth0-vue";
import { isEmpty, first, uniq, without } from "lodash";
import { format } from "date-fns";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale
} from "chart.js";
import { Bar } from "vue-chartjs";
import AirSageMap from "@/lib/map";
import { unfurlNestedDates, weekdayBucketLabels } from "@/lib/utils";
import {
  DATA_SOURCES,
  DATA_SOURCE_LABELS,
  decodeCvView,
  filterCvRecords,
  zoneTotals,
  zoneNamesFromFeatures,
  computeMatrix,
  tripsByDayAgg,
  tripsByHourAgg,
  computeTopMatrix,
  buildChoropleth,
  formatHourAgg,
  sortByBucketOrder
} from "@/lib/tripMatrix/cv";
import {
  DENSITY_UNIT,
  zoneAreas,
  matrixZoneStats,
  zoneDensities,
  computeNiceThresholds,
  colorRamp,
  stepExpression,
  connectedZones
} from "@/lib/tripMatrix/zones";
import ContentBlock from "@/components/ContentBlock.vue";
import MultipleSelect from "@/components/MultipleSelect.vue";
import SingleObjectSelect from "@/components/SingleObjectSelect.vue";
import MatrixHeatmap from "@/components/visualizations/charts/MatrixHeatmap.vue";
import IconSpinner from "@/components/icons/IconSpinner.vue";
import IconClose from "@/components/icons/IconClose.vue";
import { StudiesService } from "@/services";
import { useStudyStore } from "@/stores/study";
import { useAlertStore } from "@/stores/alert";

const studyStore = useStudyStore();
const alertStore = useAlertStore();
const route = useRoute();
const { getAccessTokenSilently } = useAuth0();
const { config, zones } = storeToRefs(studyStore);

const loaded = ref(false);
const busy = ref(false);
const dataMap = ref({
  matrix: [],
  purpose: [],
  dayAgg: [],
  hourAgg: [],
  heatmap: []
});

// data source handling; LBS is always present, CV is opt-in per study config
const dataSources = computed(() => uniq([DATA_SOURCES.LBS, ...(config.value.dataSources || [])]));
const hasCv = computed(() => dataSources.value.includes(DATA_SOURCES.CV));
const source = ref(DATA_SOURCES.LBS);
const isCv = computed(() => source.value === DATA_SOURCES.CV);
const zoneIdKey = computed(() => config.value.zonePropertyMap?.id);
const zoneNames = computed(() => zoneNamesFromFeatures(zones.value, config.value.zonePropertyMap || {}));
let cvRecords = null;
const cvMonths = ref([]);
let cvChoropleth = null;

// choropleth state: zone areas are fixed per study, densities follow the applied filter
let areas = new Map();
let densities = new Map();
let densityRamp = colorRamp([]);
const legend = ref({ title: DENSITY_UNIT, items: [] });

// zone selection: the selected zone and the zones it exchanges trips with
const selectedZoneId = ref(null);
const selection = ref(null);
let selectedPairs = new Map();

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeydown);
  studyStore.reset();
});

const formatNumber = (value) => (Number.isFinite(value) ? value.toLocaleString(undefined, { maximumFractionDigits: value < 10 ? 2 : 0 }) : "—");

const loadZones = async () => {
  try {
    const { id } = route.params;
    const token = await getAccessTokenSilently();
    zones.value = await StudiesService.getZones(id, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  } catch (error) {
    alertStore.alertError(error);
  }
};

const readMaterializedView = async (view) => {
  try {
    const { id } = route.params;
    const token = await getAccessTokenSilently();
    return await StudiesService.getMaterializedView(id, {
      headers: {
        Authorization: `Bearer ${token}`
      },
      params: view
    });
  } catch (error) {
    alertStore.alertError(error);
  }
};

const loadCvRecords = async () => {
  if (cvRecords) {
    return true;
  }
  const view = await readMaterializedView({ view: DATA_SOURCES.CV });
  if (!view) {
    return false;
  }
  cvRecords = decodeCvView(view);
  cvMonths.value = view.months || [];
  return true;
};

const formatMonth = (m) => ({ value: format(new Date(m), "yyyyMM"), name: format(new Date(m), "MMM yyyy") });
const studyMonths = computed(() => unfurlNestedDates(config.value.dates || []).map(formatMonth));
const months = computed(() => {
  if (!isCv.value) {
    return studyMonths.value;
  }
  const available = studyMonths.value.filter((m) => cvMonths.value.includes(m.value));
  if (!isEmpty(available)) {
    return available;
  }
  return cvMonths.value.map((m) => formatMonth(`${m.slice(0, 4)}-${m.slice(4)}-01T12:00:00`));
});
const tripPurposes = ref([]);
const dayAggs = ref([]);
const hourAggs = ref([]);

const setSource = async (next) => {
  if (next === source.value || busy.value) {
    return;
  }
  busy.value = true;
  try {
    if (next === DATA_SOURCES.CV && !await loadCvRecords()) {
      return;
    }
    source.value = next;
    if (!months.value.some((m) => m.value === filter.value.month)) {
      filter.value.month = first(months.value)?.value;
    }
    await applyFilter();
  } finally {
    busy.value = false;
  }
};

onMounted(async () => {
  await loadZones();
  areas = zoneAreas(zones.value, zoneIdKey.value);
  tripPurposes.value = uniq(Object.values(config.value.tripPurposeBuckets));
  dayAggs.value = weekdayBucketLabels(config.value.weekdayBuckets);
  hourAggs.value = without(uniq(Object.values(config.value.hourBuckets)), "Omit", "omit");
  loaded.value = true;
  await nextTick();
  filter.value.month = first(months.value).value;
  filter.value.tripPurposes = tripPurposes.value;
  filter.value.dayAgg = dayAggs.value;
  filter.value.hourAgg = hourAggs.value;
  window.addEventListener("keydown", onKeydown);
  loadMap();
});

const ZONE_SHAPE_LAYER_ID = "zoneShapes";
const ZONE_LINE_LAYER_ID = "zoneLines";
const ZONE_SOURCE_ID = "zones";
const SELECTED_COLOR = "#1675D1";
const NO_ZONE_FILTER = ["==", ["to-string", ["get", "zone_id"]], ""];
let map = null;
let hoverStateId;

const zoneIdExpression = ["to-string", ["get", "zone_id"]];

/** Fill colour driven by a per-zone feature-state value (density, or trips paired with the selected zone). */
const fillColorExpression = (stateKey, ramp) => [
  "case",
  ["boolean", ["feature-state", "selected"], false],
  SELECTED_COLOR,
  stepExpression(["to-number", ["feature-state", stateKey]], ramp)
];

const filter = ref({
  month: "",
  tripPurposes: [],
  dayAgg: [],
  hourAgg: []
});

async function applyFilter() {
  try {
    if (isCv.value) {
      if (!await loadCvRecords()) {
        return;
      }
      const filtered = filterCvRecords(cvRecords, {
        month: filter.value.month,
        dayAgg: filter.value.dayAgg,
        hourAgg: filter.value.hourAgg
      });
      dataMap.value.matrix = computeMatrix(filtered, zoneNames.value);
      dataMap.value.purpose = [];
      dataMap.value.dayAgg = tripsByDayAgg(filtered, dayAggs.value);
      dataMap.value.hourAgg = tripsByHourAgg(filtered, hourAggs.value);
      dataMap.value.heatmap = computeTopMatrix(cvRecords, filtered, zoneNames.value);
      cvChoropleth = buildChoropleth(zones.value, zoneTotals(filtered), zoneIdKey.value);
      refreshChoropleth();
      return;
    }

    const proxyFilter = {
      month: filter.value.month,
      tripPurposes: filter.value.tripPurposes,
      dayAgg: filter.value.dayAgg,
      hourAgg: filter.value.hourAgg
    };
    const data = await Promise.all([
      readMaterializedView({ name: "getMatrix", ...proxyFilter }),
      readMaterializedView({ name: "getTripsByPurpose", ...proxyFilter }),
      readMaterializedView({ name: "getTripsByDayAgg", ...proxyFilter }),
      readMaterializedView({ name: "getTripsByHourAgg", ...proxyFilter }),
      readMaterializedView({ name: "getTopMatrix", ...proxyFilter })
    ]);
    dataMap.value.matrix = data.shift() || [];
    dataMap.value.purpose = data.shift() || [];
    // the reports come back in query order; list the buckets in the order the filter shows them
    dataMap.value.dayAgg = sortByBucketOrder(data.shift(), "day_agg", dayAggs.value);
    dataMap.value.hourAgg = sortByBucketOrder(data.shift(), "hour_agg", hourAggs.value);
    dataMap.value.heatmap = data.shift() || [];
    refreshChoropleth();
  } catch (error) {
    alertStore.alertError(error);
  }
}

/** Recompute the density ramp from the applied matrix and redraw the zone layers. */
function refreshChoropleth() {
  densities = zoneDensities(matrixZoneStats(dataMap.value.matrix), areas);
  densityRamp = colorRamp(computeNiceThresholds(densities.values()));
  loadLayers();
}

// feature-state target for the active zone source; the LBS vector tiles need a
// source layer, the CV GeoJSON source does not
const zoneFeature = (id) => (isCv.value ? { source: ZONE_SOURCE_ID, id } : { source: ZONE_SOURCE_ID, sourceLayer: "trip-matrix", id });

/** Attach each zone's density so the fill colour can be computed without the tiles carrying it. */
function applyDensityState() {
  for (const [id, density] of densities) {
    map.setFeatureState(zoneFeature(id), { density });
  }
}

function showDensityLegend() {
  legend.value = { title: DENSITY_UNIT, items: densityRamp.legend };
}

function onKeydown(event) {
  if (event.key === "Escape" && selectedZoneId.value !== null) {
    clearSelection();
  }
}

/** Show only the zones exchanging trips with the given zone, coloured by those trips. */
function selectZone(id) {
  const zoneId = String(id);
  if (selectedZoneId.value !== null) {
    resetSelectionState();
  }
  const connected = connectedZones(dataMap.value.matrix, zoneId);
  const name = zoneNames.value[zoneId] ?? zoneId;
  selectedZoneId.value = zoneId;
  selectedPairs = connected.pairs;
  selection.value = {
    zoneId,
    name,
    total: connected.total,
    inbound: connected.inbound,
    outbound: connected.outbound,
    intrazonal: connected.intrazonal,
    connected: connected.connected
  };

  if (!map || !map.getLayer(ZONE_SHAPE_LAYER_ID)) {
    return;
  }
  const pairRamp = colorRamp(computeNiceThresholds(Array.from(selectedPairs.values(), (p) => p.total)));
  for (const [other, pair] of selectedPairs) {
    map.setFeatureState(zoneFeature(other), { pair: pair.total });
  }
  map.setFeatureState(zoneFeature(zoneId), { selected: true });
  map.setFilter(ZONE_SHAPE_LAYER_ID, ["in", zoneIdExpression, ["literal", [zoneId, ...selectedPairs.keys()]]]);
  map.setFilter(ZONE_LINE_LAYER_ID, ["==", zoneIdExpression, zoneId]);
  map.setPaintProperty(ZONE_SHAPE_LAYER_ID, "fill-color", fillColorExpression("pair", pairRamp));
  legend.value = { title: `Trips to/from ${name}`, items: pairRamp.legend };
}

/** Drop the per-pair feature state and filters of the current selection (layers must exist). */
function resetSelectionState() {
  if (!map || !map.getLayer(ZONE_SHAPE_LAYER_ID)) {
    return;
  }
  for (const other of selectedPairs.keys()) {
    map.removeFeatureState(zoneFeature(other), "pair");
  }
  if (selectedZoneId.value !== null) {
    map.removeFeatureState(zoneFeature(selectedZoneId.value), "selected");
  }
  map.setFilter(ZONE_SHAPE_LAYER_ID, null);
  map.setFilter(ZONE_LINE_LAYER_ID, NO_ZONE_FILTER);
  map.setPaintProperty(ZONE_SHAPE_LAYER_ID, "fill-color", fillColorExpression("density", densityRamp));
}

function clearSelection() {
  resetSelectionState();
  selectedZoneId.value = null;
  selection.value = null;
  selectedPairs = new Map();
  showDensityLegend();
}

let zoneEventsBound = false;
const bindZoneEvents = () => {
  if (zoneEventsBound) {
    return;
  }
  zoneEventsBound = true;

  map.on("mousemove", ZONE_SHAPE_LAYER_ID, (e) => {
    const [feature] = e.features;
    if (feature) {
      const { id } = feature;
      if (id !== undefined && id !== null) {
        map.getCanvas().style.cursor = "pointer";
        if (hoverStateId !== id) {
          if (hoverStateId !== undefined && hoverStateId !== null) {
            map.setFeatureState(zoneFeature(hoverStateId), { hover: false });
          }

          hoverStateId = id;
          map.setFeatureState(zoneFeature(hoverStateId), { hover: true });
        }
      }
    }
  });

  map.on("mouseleave", ZONE_SHAPE_LAYER_ID, () => {
    map.getCanvas().style.cursor = "";
    if (hoverStateId !== undefined && hoverStateId !== null) {
      map.setFeatureState(zoneFeature(hoverStateId), { hover: false });
    }
    hoverStateId = null;
  });

  map.on("click", ZONE_SHAPE_LAYER_ID, (e) => {
    const [feature] = e.features;
    if (feature && feature.id !== undefined && feature.id !== null) {
      selectZone(feature.id);
    }
  });
};

const loadLayers = async () => {
  const { id } = route.params;
  if (map) {
    hoverStateId = null;
    [ZONE_SHAPE_LAYER_ID, ZONE_LINE_LAYER_ID].forEach((id) => {
      if (map.getLayer(id)) {
        map.removeLayer(id);
      }
    });

    if (map.getSource(ZONE_SOURCE_ID)) {
      map.removeSource(ZONE_SOURCE_ID);
    }

    if (isCv.value) {
      map.addSource(ZONE_SOURCE_ID, {
        type: "geojson",
        data: cvChoropleth,
        promoteId: "zone_id"
      });
    } else {
      map.addSource(ZONE_SOURCE_ID, {
        type: "vector",
        tiles: [
          `${import.meta.env.VITE_MARTIN_URL}/get_inbound_outbound_trips/{z}/{x}/{y}?${new URLSearchParams([
            ...Object.entries({
              id,
              month: filter.value.month,
              trip_purposes: `{${filter.value.tripPurposes}}`,
              local_day_aggs: `{${filter.value.dayAgg}}`,
              local_hour_aggs: `{${filter.value.hourAgg}}`
            })
          ]).toString()}`
        ],
        promoteId: "zone_id",
        minzoom: 0,
        maxzoom: 8
      });
    }

    const sourceLayer = isCv.value ? {} : { "source-layer": "trip-matrix" };
    map.addLayer({
      id: ZONE_SHAPE_LAYER_ID,
      type: "fill",
      source: ZONE_SOURCE_ID,
      ...sourceLayer,
      paint: {
        "fill-color": fillColorExpression("density", densityRamp),
        "fill-opacity": ["case", ["boolean", ["feature-state", "hover"], false], 0.92, 0.67]
      }
    });
    map.addLayer({
      id: ZONE_LINE_LAYER_ID,
      type: "line",
      source: ZONE_SOURCE_ID,
      ...sourceLayer,
      filter: NO_ZONE_FILTER,
      paint: {
        "line-color": SELECTED_COLOR,
        "line-width": 2.5
      }
    });

    bindZoneEvents();
    // the source was rebuilt, so its feature state has to be attached again
    applyDensityState();
    if (selectedZoneId.value !== null) {
      selectZone(selectedZoneId.value);
    } else {
      showDensityLegend();
    }

    map.centerOn(zones.value, { easing: () => 1 });
  }
};

const mapElement = ref(null);
const loadMap = () => {
  map = new AirSageMap(mapElement.value, {
    eventHandlers: {
      "style.load": applyFilter
    }
  });
};

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale);
const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  borderRadius: 4,
  scales: {
    x: {
      grid: {
        display: false
      }
    },
    y: {
      display: false
    }
  },
  plugins: {
    legend: {
      display: false
    }
  }
};

// trip purpose data
const purposeChartData = computed(() => {
  return {
    labels: dataMap.value.purpose.map((t) => t.trip_purpose),
    datasets: [{
      backgroundColor: "#2171B5",
      data: dataMap.value.purpose.map((t) => t.total)
    }]
  };
});

// day agg data
const dayAggChartData = computed(() => {
  return {
    labels: dataMap.value.dayAgg.map((t) => t.day_agg),
    datasets: [{
      backgroundColor: "#2171B5",
      data: dataMap.value.dayAgg.map((t) => t.total)
    }]
  };
});

// hour data
const hourAggChartData = computed(() => {
  return {
    labels: dataMap.value.hourAgg.map((t) => formatHourAgg(t.hour_agg)),
    datasets: [{
      backgroundColor: "#2171B5",
      data: dataMap.value.hourAgg.map((t) => t.total)
    }]
  };
});
</script>
